/**
 * PATH: packages/bills/src/components/captureRouting.ts
 * FILE_ID: BILLS-PKG-CMP-CAPROUTE-001
 * PURPOSE: Pure decisions for the "pick a value off the PDF" interaction so the
 *          behaviour is testable without rendering the whole workspace.
 * WHY: The feature recurred 4× (operators "still cannot select and have it
 *      fill"). Root cause was never the pick mechanism — it was the rigid,
 *      hidden interaction model (you HAD to arm a field first; there was no
 *      value-first order; a press that moved a little was ambiguous with pan).
 *      These tiny I→O functions encode the new, forgiving model:
 *        - a press that barely moves is a CLICK (pick); a bigger move is a pan;
 *        - a pick fills the armed field if there is one, else it is STASHED;
 *        - arming/focusing a field while a value is stashed drops it in.
 *
 * FLOW:
 *   (dx,dy)            ─▶ isClickGesture → pick vs pan
 *   (armedField,text)  ─▶ routePickedText → {fill | stash}
 *   (field,stash)      ─▶ takeStashForField → value to apply | null
 *
 * RELATED:
 *   - pdfTextCapture.tsx (CapturePageLayer): uses isClickGesture.
 *   - BookkeepingWorkspace.tsx: uses routePickedText + takeStashForField.
 */

import type { BillFieldKey } from './billFieldKeys';

/**
 * Max pointer travel (CSS px) between mousedown and mouseup that still counts
 * as a "click" (= pick the run under the cursor). Anything larger is a drag,
 * which the scroll container treats as a pan — so picking and panning coexist
 * on the same always-on overlay without a separate mode toggle.
 */
export const PICK_MOVE_THRESHOLD = 5;

/** f(dx,dy) → true when the gesture is a click (pick), not a drag (pan). */
export function isClickGesture(dx: number, dy: number, threshold = PICK_MOVE_THRESHOLD): boolean {
  return Math.abs(dx) <= threshold && Math.abs(dy) <= threshold;
}

export type PickRoute =
  | { type: 'fill'; field: BillFieldKey; value: string }
  /**
   * Open the in-place editor over the field's highlight box on the PDF.
   * ``value`` is the prefill: the DRAGGED text for drag gestures (visible
   * confirmation before it commits), or null for clicks (the caller prefills
   * the field's CURRENT value so the operator just types the correction).
   */
  | { type: 'edit'; field: BillFieldKey; value: string | null }
  | { type: 'stash'; value: string };

/**
 * f(armedField, value, regionField, armedIsAuto, gesture) → where a
 * freshly-picked value goes.
 *   1. Pick landed inside a KNOWN field region and the operator hasn't
 *      explicitly armed a DIFFERENT field → open the in-place EDITOR on that
 *      region (2026-07-24 "this edit function still does not work": picking
 *      can only re-copy printed text — silently REPLACING the whole field
 *      with whatever run was clicked. The audit trail showed every such
 *      "edit" being undone seconds later. Editing in place is what the
 *      operator was actually reaching for). Clicks prefill the current
 *      value; drags prefill the dragged text so a partial sweep is visible
 *      before it commits instead of clobbering the field.
 *   2. A DRAG with an EXPLICITLY armed field (operator picked the field
 *      themselves) → fill it; a sweep is a deliberate "copy this text into
 *      my field" gesture.
 *   3. Everything else → STASH the value; the next field the operator
 *      clicks receives it. A bare click NEVER overwrites a field anymore:
 *      merely having a sidebar input focused counts as "armed", so
 *      click-to-fill meant any stray click on the document replaced the
 *      whole line item (2026-07-24 "when i try to click on something else
 *      … it replaces the entire line item — need to remove this select to
 *      update"). Auto-armed fields (selected by a previous region pick)
 *      never receive picks implicitly either.
 */
export function routePickedText(
  armedField: BillFieldKey | null,
  value: string,
  regionField: BillFieldKey | null = null,
  armedIsAuto = false,
  gesture: 'click' | 'drag' = 'click',
): PickRoute {
  if (regionField && (!armedField || armedIsAuto || armedField === regionField)) {
    return { type: 'edit', field: regionField, value: gesture === 'drag' ? value : null };
  }
  if (armedField && !armedIsAuto && gesture === 'drag') {
    return { type: 'fill', field: armedField, value };
  }
  return { type: 'stash', value };
}

/**
 * f(field, stash) → the value to drop into `field` when it becomes armed, or
 * null when there's nothing stashed. Lets value-first picking complete: pick a
 * value (stashed), then click the field to fill it.
 */
export function takeStashForField(
  _field: BillFieldKey,
  stash: string | null,
): string | null {
  return stash != null ? stash : null;
}
